import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type SavedItemType = 'provider' | 'medication' | 'procedure' | 'care-plan' | 'list';

export interface SavedItem {
  id: string;
  type: SavedItemType;
  title: string;
  subtitle?: string;
  savedAt: Date;
  data: any; // The actual saved object (provider, medication, etc.)
}

interface SavedItemsContextType {
  savedItems: SavedItem[];
  saveItem: (item: SavedItem) => void;
  removeItem: (id: string, type: SavedItemType) => void;
  isItemSaved: (id: string, type: SavedItemType) => boolean;
  getSavedItemsByType: (type: SavedItemType) => SavedItem[];
}

const SavedItemsContext = createContext<SavedItemsContextType | undefined>(undefined);

const STORAGE_KEY = 'mario_saved_items';

export const SavedItemsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [savedItems, setSavedItems] = useState<SavedItem[]>([]);

  // Load saved items from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Convert savedAt strings back to Date objects
        const items = parsed.map((item: any) => ({
          ...item,
          savedAt: new Date(item.savedAt)
        }));
        setSavedItems(items);
      }
    } catch (error) {
      console.error('Error loading saved items:', error);
    }
  }, []);

  // Save to localStorage whenever savedItems changes
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(savedItems));
    } catch (error) {
      console.error('Error saving items:', error);
    }
  }, [savedItems]);

  const saveItem = (item: SavedItem) => {
    setSavedItems(prev => {
      // Remove existing item if it exists (to update savedAt time)
      const filtered = prev.filter(i => !(i.id === item.id && i.type === item.type));
      // Add new item at the beginning
      return [item, ...filtered];
    });
  };

  const removeItem = (id: string, type: SavedItemType) => {
    setSavedItems(prev => prev.filter(item => !(item.id === id && item.type === type)));
  };

  const isItemSaved = (id: string, type: SavedItemType): boolean => {
    return savedItems.some(item => item.id === id && item.type === type);
  };

  const getSavedItemsByType = (type: SavedItemType): SavedItem[] => {
    return savedItems.filter(item => item.type === type);
  };

  return (
    <SavedItemsContext.Provider value={{
      savedItems,
      saveItem,
      removeItem,
      isItemSaved,
      getSavedItemsByType
    }}>
      {children}
    </SavedItemsContext.Provider>
  );
};

export const useSavedItems = () => {
  const context = useContext(SavedItemsContext);
  if (context === undefined) {
    throw new Error('useSavedItems must be used within a SavedItemsProvider');
  }
  return context;
};